/**
 * boardFilter — pure scope/member filtering and sectioning for the board.
 *
 * The board renders one payload (`GET /api/chores/board`, every live instance
 * across both kinds) through three lenses:
 *
 *   Mine   — what the viewer is personally holding: their claimed and
 *            awaiting-approval cards. Not "assigned to me" — a card only
 *            becomes yours by claiming it.
 *   Ledige — what anyone could pick up right now (status 'available').
 *   Alle   — every live card, including ones a sibling has claimed.
 *
 * Kept as pure functions rather than inline `.filter()` chains in the route so
 * the semantics are unit-testable without a render harness — the same reason
 * homeworkDue and recurrenceHint are separate modules.
 *
 * Seksjonene (#154, steg 4b): Mine nå → Ledige → Lekser (`sectionBoard`).
 *
 * Regelen for «Ta flytter aldri kortet» (design/HANDOFF.md, MAPPING.md §5.6):
 * et kort du trykker «Ta» på, blir *festet* (`pinned`). Et festet kort
 *   - blir værende i seksjonen og på plassen det hadde (serveren sorterer
 *     etter frist og så opprettelse, ikke etter status, så plassen står),
 *   - slipper gjennom utvalget (Ledige og personfilteret), selv om det ikke
 *     lenger er ledig,
 *   - og havner derfor ikke i «Mine nå» så lenge det er festet.
 * Festet gjelder til tavla monteres på nytt (navigasjon) eller utvalget
 * endres; da legger kortet seg i «Mine nå». Det løsnes også hvis claimet
 * feiler. En ny henting av tavla løsner det ikke: claimet selv gir to (svaret
 * og WS-rammen `chores`), så en regel som løsnet på henting ville flyttet
 * kortet i det øyeblikket det ble ditt.
 */
import type { BoardChoreInstance } from './useChoresQuery';

export type BoardScope = 'mine' | 'open' | 'all';

/** Statuses that mean "somebody is personally holding this card". */
const HELD = new Set(['claimed', 'awaiting_approval']);

/** True when this row is the viewer's own in-flight work. */
function isHeldBy(row: BoardChoreInstance, memberId: string): boolean {
  return row.claimed_by === memberId && HELD.has(row.status);
}

/**
 * Is this card relevant to `memberId`? Either they are holding it, or it is
 * still available and they are eligible — an open card (no assignees) counts as
 * everyone's, which is what makes the pool a pool.
 */
function isRelevantTo(row: BoardChoreInstance, memberId: string): boolean {
  if (isHeldBy(row, memberId)) return true;
  if (row.status !== 'available') return false;
  const assignees = row.assignee_ids ?? [];
  return assignees.length === 0 || assignees.includes(memberId);
}

export interface FilterOpts {
  scope: BoardScope;
  /** The signed-in member; '' when the session has not resolved yet. */
  viewerId: string;
  /** Optional "who" chip — null means everyone. Ignored when scope is 'mine'. */
  memberId?: string | null;
  /** Kort som er festet der de står (se regelen øverst). */
  pinned?: ReadonlySet<string>;
}

/**
 * Apply scope, then the optional member chip. Input order is preserved
 * (the server already sorted deadline-first), so callers never re-sort.
 */
export function filterBoard(
  rows: BoardChoreInstance[],
  { scope, viewerId, memberId, pinned }: FilterOpts,
): BoardChoreInstance[] {
  let out = rows;
  const isPinned = (r: BoardChoreInstance): boolean => pinned?.has(r.id) ?? false;

  if (scope === 'mine') {
    // A viewer-less session has no personal work by definition; returning the
    // whole board here would show a kid every sibling's card under "Mine".
    return viewerId ? rows.filter((r) => isHeldBy(r, viewerId)) : [];
  }
  if (scope === 'open') {
    out = out.filter((r) => r.status === 'available' || isPinned(r));
  }

  // The chip is only offered for 'open'/'all' — under 'mine' it would either be
  // a no-op (the viewer) or guarantee an empty list (anyone else).
  if (memberId) {
    out = out.filter((r) => isRelevantTo(r, memberId) || isPinned(r));
  }
  return out;
}

export interface BoardSections {
  /** «Mine nå»: det seeren holder på med, begge slag, unntatt festede kort. */
  mine: BoardChoreInstance[];
  /** «Ledige»: resten av oppgavene, også de et søsken har tatt. */
  open: BoardChoreInstance[];
  /** «Lekser»: resten av leksene. */
  homework: BoardChoreInstance[];
}

/**
 * Del et (allerede filtrert) utvalg i seksjonene Mine nå → Ledige → Lekser.
 * Rekkefølgen fra serveren beholdes i hver seksjon. Et festet kort blir i
 * Ledige eller Lekser selv når det er ditt (regelen øverst i fila).
 */
export function sectionBoard(
  rows: BoardChoreInstance[],
  { viewerId, pinned }: { viewerId: string; pinned?: ReadonlySet<string> },
): BoardSections {
  const out: BoardSections = { mine: [], open: [], homework: [] };
  for (const r of rows) {
    if (viewerId && isHeldBy(r, viewerId) && !pinned?.has(r.id)) out.mine.push(r);
    else if (r.kind === 'homework') out.homework.push(r);
    else out.open.push(r);
  }
  return out;
}

export interface CategoryGroup {
  /** Template category, or null for the catch-all group. */
  key: string | null;
  rows: BoardChoreInstance[];
}

/**
 * Group chores by template category, preserving first-appearance order so the
 * server's deadline sort still drives which group leads. Uncategorised rows
 * collect into a single trailing null group.
 *
 * Returns a single null-keyed group when nothing has a category — which is the
 * state of every existing template, since `category` has been in the schema and
 * the create/update bodies all along but was never surfaced in any UI. Callers
 * should skip group headings when there is only one group.
 */
export function groupByCategory(rows: BoardChoreInstance[]): CategoryGroup[] {
  const byKey = new Map<string, BoardChoreInstance[]>();
  const uncategorised: BoardChoreInstance[] = [];

  for (const r of rows) {
    const cat = r.category?.trim();
    if (!cat) {
      uncategorised.push(r);
      continue;
    }
    const list = byKey.get(cat);
    if (list) list.push(r);
    else byKey.set(cat, [r]);
  }

  const groups: CategoryGroup[] = [...byKey.entries()].map(([key, rs]) => ({ key, rows: rs }));
  if (uncategorised.length > 0) groups.push({ key: null, rows: uncategorised });
  return groups;
}
